import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Search, CheckCheck, X, Loader2 } from "lucide-react";
import { notifView } from "@/components/NotificationsPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [["all", "All"], ["unread", "Unread"], ["action", "Action required"], ["history", "History"]];
const PRIO = {
  critical: "bg-rose-600 text-white", high: "bg-amber-500 text-white",
  normal: "bg-slate-100 text-slate-600", low: "bg-slate-50 text-slate-400",
};
const TYPES = [["", "All types"], ["department_move", "Job handovers"], ["order_authorisation", "Order authorisations"], ["order_query", "Order queries"],
  ["absence_authorisation", "Absence approvals"], ["holiday_decision", "Holiday decisions"], ["run_disruption", "Run disruptions"],
  ["short_pick", "Short picks"], ["feedback", "Feedback"], ["attachment_scan_alert", "Security alerts"], ["operational_alert", "Operational alerts"],
  ["sales_enquiry_new", "New enquiries"], ["sales_customer_reply", "Customer replies"], ["sales_quote_decision", "Quote decisions"],
  ["job_new", "New shifts"], ["job_change", "Shift changes"], ["load_signed_off", "Van sign-offs"], ["fin_refund", "Refunds"],
  ["admin_integration_failure", "Integration failures"], ["load_ready", "Ready for loading"], ["load_started", "Loading started"],
  ["load_missing", "Missing products"], ["load_discrepancy", "Loading discrepancies"], ["load_assigned", "Loading assignments"],
  ["pick_assigned", "Picking assignments"], ["vehicle_assignment", "Vehicle crew changes"], ["ticket_new", "New tickets"],
  ["ticket_update", "Ticket updates"], ["sales_quote_followup", "Quote follow-ups"], ["engineer_late", "Engineer not arrived"],
  ["reminder", "Reminders"], ["escalation", "Escalations"]];

function dayLabel(ts) {
  const d = new Date(ts); const t = new Date(); t.setHours(0, 0, 0, 0);
  const diff = (t - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000;
  return diff <= 0 ? "Today" : diff === 1 ? "Yesterday" : "Earlier";
}

function Row({ n, onRead, onDismiss }) {
  const v = notifView(n);
  const actionOpen = n.kind === "action" && n.action_status === "open";
  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 border-b border-slate-100 last:border-0 ${n.read ? "" : "bg-indigo-50/30"}`} data-testid={`nc-row-${n.id}`}>
      <span className="mt-0.5 w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><v.Icon className="w-4 h-4" /></span>
      <Link to={n.entity?.link || v.to} onClick={() => onRead(n)} className="min-w-0 flex-1 group">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[#1D1D1F] group-hover:text-indigo-700">{v.title}</span>
          {n.priority !== "normal" && <span className={`text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 ${PRIO[n.priority]}`} data-testid={`nc-priority-${n.id}`}>{n.priority}</span>}
          {n.kind === "action" && <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${actionOpen ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`nc-action-${n.id}`}>{actionOpen ? "Action required" : "Resolved"}</span>}
        </div>
        {v.sub && <div className="text-[12px] text-[#6E6E73] mt-0.5">{v.sub}</div>}
        {v.note && <div className="text-[12px] text-slate-600 mt-0.5 line-clamp-2">{v.note}</div>}
        <div className="text-[11px] text-slate-400 mt-1">{new Date(n.ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{n.department ? ` · ${n.department}` : ""}</div>
      </Link>
      {!n.read && <span className="mt-2 w-2 h-2 rounded-full bg-indigo-600 shrink-0" />}
      {!actionOpen && (
        <button onClick={() => onDismiss(n)} title="Dismiss" data-testid={`nc-dismiss-${n.id}`} className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100"><X className="w-4 h-4" /></button>
      )}
    </div>
  );
}

export default function NotificationCentre() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [priority, setPriority] = useState("");
  const [data, setData] = useState({ items: [], unread: 0, action_open: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/notifications", { params: { tab, q: q || undefined, type: type || undefined, priority: priority || undefined, limit: 200 } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load notifications")).finally(() => setLoading(false));
  }, [tab, q, type, priority]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const onRead = (n) => { if (!n.read) api.post(`/notifications/${n.id}/read`).catch(() => {}); };
  const onDismiss = (n) => api.post(`/notifications/${n.id}/dismiss`).then(load).catch((e) => toast.error(e.response?.data?.detail || "Could not dismiss"));
  const readAll = () => api.post("/notifications/read-all").then(load);

  const groups = ["Today", "Yesterday", "Earlier"].map((g) => [g, data.items.filter((n) => dayLabel(n.ts) === g)]).filter(([, xs]) => xs.length);

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="notification-centre-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Bell className="w-7 h-7 text-[#6E6E73]" />
        <div className="flex-1">
          <h1 className="ted-page-title">Notifications</h1>
          <p className="ted-subtle mt-1" data-testid="nc-counts">{data.unread} unread · {data.action_open} need action</p>
        </div>
        <button onClick={readAll} data-testid="nc-mark-all-read" className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline"><CheckCheck className="w-4 h-4" /> Mark all as read</button>
      </div>
      <div className="bg-white rounded-[18px] border border-black/[0.04] shadow-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
          <div className="flex rounded-full bg-slate-100 p-1 max-w-full overflow-x-auto">
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`nc-tab-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${tab === k ? "bg-white shadow-sm text-[#1D1D1F]" : "text-[#6E6E73] hover:text-[#1D1D1F]"}`}>{l}</button>
            ))}
          </div>
          <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[190px] rounded-lg" data-testid="nc-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"} data-testid={`nc-type-${v || "all"}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[150px] rounded-lg" data-testid="nc-filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["all", "All priorities"], ["critical", "Critical"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]].map(([v, l]) => (
                <SelectItem key={v} value={v} data-testid={`nc-priority-opt-${v}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notifications…" data-testid="nc-search" className="w-full h-9 pl-9 pr-3 rounded-full bg-slate-100 text-sm outline-none" />
          </div>
        </div>
        {loading ? <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          : groups.length === 0 ? <div className="py-16 text-center text-sm text-slate-400" data-testid="nc-empty">Nothing here.</div>
          : groups.map(([g, xs]) => (
            <div key={g} data-testid={`nc-group-${g.toLowerCase()}`}>
              <div className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g}</div>
              {xs.map((n) => <Row key={n.id} n={n} onRead={onRead} onDismiss={onDismiss} />)}
            </div>
          ))}
      </div>
    </div>
  );
}
